import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import jsQR from "jsqr";
import { posAPI } from "../../services/api";
import { useI18n } from "../../i18n";
import { formatMoney } from "../utils/money";

/**
 * Attaching a sale to the customer who made it.
 *
 * Part of the ticket rather than the register's header: a card belongs to one
 * basket, so it is offered once there is a basket to attach it to, and it
 * leaves with that basket.
 *
 * Two rules from behind an actual counter: it has to be skippable in one tap,
 * because most sales have no card and a queue-blocking step gets abandoned
 * inside a week; and typing the code by hand must always work, because the
 * camera will fail on a cracked screen in bad light.
 */
const CustomerScan = ({ customer, onPicked, onCleared, currency }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const owed = Number(customer?.credit_balance) || 0;

  const aboutCustomer = () => {
    const visits = Number(customer.purchase_count);

    if (Number.isFinite(visits) && visits > 0) {
      return t("pos.customer.purchasesBefore", { count: visits });
    }

    if (Number.isFinite(visits)) return t("pos.customer.firstPurchase");

    return customer.phone_hint || t("pos.customer.linked");
  };

  return (
    <div className="px-4 py-3 border-t border-slate-800">
      {customer ? (
        <div className="flex items-center gap-3">
          <span
            className="shrink-0 w-9 h-9 rounded-full bg-primary-600/25 border border-primary-500 flex items-center justify-center"
            aria-hidden="true"
          >
            👤
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate">
              {customer.name}
            </p>
            <p className="text-xs text-slate-400 truncate">{aboutCustomer()}</p>
            {owed > 0 && (
              <p className="text-xs font-semibold text-amber-400 truncate">
                {t("pos.customer.owes", { amount: formatMoney(owed, currency) })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onCleared}
            className="shrink-0 h-9 px-3 rounded-lg bg-slate-800 border border-slate-700 text-xs font-bold text-slate-300 hover:bg-slate-700"
          >
            {t("common.remove")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full h-11 rounded-xl border border-dashed border-slate-600 text-sm font-semibold text-slate-300 hover:bg-slate-800 active:scale-[0.99] transition-all flex items-center justify-center gap-2"
        >
          <span aria-hidden="true">👤</span> {t("pos.customer.add")}
          <span className="font-normal text-slate-500">
            · {t("common.optional")}
          </span>
        </button>
      )}

      {open && <ScanSheet onClose={() => setOpen(false)} onPicked={onPicked} />}
    </div>
  );
};

const ScanSheet = ({ onClose, onPicked }) => {
  const { t } = useI18n();
  const [mode, setMode] = useState("camera");
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const pick = async (customer) => {
    onPicked(customer);
    onClose();
  };

  const lookup = async (value) => {
    if (!value) return;

    setBusy(true);
    setError("");

    try {
      const { data } = await posAPI.resolveCustomer(value.trim());
      await pick(data);
    } catch (err) {
      setError(
        err.response?.status === 404
          ? t("pos.customer.notRecognised")
          : t("pos.customer.checkFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  const byPhone = async () => {
    setBusy(true);
    setError("");

    try {
      const { data } = await posAPI.findCustomer(phone);
      setMatches(data);
      if (data.length === 0) setError(t("pos.customer.nobody"));
    } catch (err) {
      setError(err.response?.data?.message || t("pos.customer.searchFailed"));
    } finally {
      setBusy(false);
    }
  };

  // Decoded with jsQR (pure JS) rather than the browser's own
  // BarcodeDetector — Safari has never implemented it, and even where it
  // exists (some Chromium builds), a missing check used to swap silently to
  // typing before the camera ever opened, with nothing on screen to say why.
  // Same approach as customer-web's shop scanner, which hit this first.
  useEffect(() => {
    if (mode !== "camera") return;

    let stopped = false;

    const stopCamera = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };

    const tick = () => {
      if (stopped) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const found = jsQR(frame.data, frame.width, frame.height);

        if (found?.data) {
          stopped = true;
          // A QR may hold the whole card URL; take the code off the end
          const raw = found.data.trim();
          const value = raw.includes("/") ? raw.split("/").pop() : raw;
          lookup(value);
          return;
        }
      }

      requestAnimationFrame(tick);
    };

    if (!navigator.mediaDevices?.getUserMedia) {
      setError(t("pos.customer.cameraUnsupported"));
      setMode("manual");
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (stopped) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }

        requestAnimationFrame(tick);
      })
      .catch(() => {
        setError(t("pos.customer.cameraDenied"));
        setMode("manual");
      });

    return () => {
      stopped = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Portalled and above the ticket sheet it is opened from (z-50)
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-slate-950/85 text-slate-100 flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-sm max-h-full overflow-y-auto bg-slate-900 border border-slate-700 rounded-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between mb-4">
          <p className="text-slate-200 font-semibold">
            {t("pos.customer.scanTitle")}
          </p>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700"
            aria-label={t("common.close")}
          >
            ✕
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-400 mb-3" aria-live="polite">
            {error}
          </p>
        )}

        {mode === "camera" && (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              className="w-full aspect-square rounded-xl bg-slate-950 object-cover mb-3"
            />
            <p className="text-sm text-slate-400 text-center mb-3">
              {t("pos.customer.pointCamera")}
            </p>
          </>
        )}

        <canvas ref={canvasRef} className="hidden" />

        {mode === "manual" && (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400">
                {t("pos.customer.cardCode")}
              </label>
              <div className="flex gap-2 mt-1">
                <input
                  type="text"
                  autoFocus
                  dir="ltr"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t("pos.customer.codePlaceholder")}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 tracking-widest uppercase"
                />
                <button
                  onClick={() => lookup(code)}
                  disabled={!code || busy}
                  className="px-4 rounded-lg bg-primary-600 text-white font-semibold disabled:opacity-50"
                >
                  {t("pos.customer.find")}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-400">
                {t("pos.customer.phoneDigits")}
              </label>
              <div className="flex gap-2 mt-1">
                <input
                  type="tel"
                  inputMode="numeric"
                  dir="ltr"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("pos.customer.phonePlaceholder")}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100"
                />
                <button
                  onClick={byPhone}
                  disabled={phone.length < 4 || busy}
                  className="px-4 rounded-lg bg-slate-700 text-slate-100 font-semibold disabled:opacity-50"
                >
                  {t("pos.customer.searchButton")}
                </button>
              </div>
            </div>

            {matches.length > 0 && (
              <ul className="border border-slate-700 rounded-lg divide-y divide-slate-800 overflow-hidden">
                {matches.map((match) => (
                  <li key={match.id}>
                    <button
                      onClick={() => pick(match)}
                      className="w-full text-start px-3 py-2 hover:bg-slate-800 flex justify-between gap-2"
                    >
                      <span className="text-slate-100">{match.name}</span>
                      <span className="text-slate-400 text-sm shrink-0" dir="ltr">
                        {match.phone_hint}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => {
              setError("");
              setMode(mode === "camera" ? "manual" : "camera");
            }}
            className="flex-1 h-11 rounded-xl border border-slate-700 text-slate-300 text-sm"
          >
            {mode === "camera"
              ? t("pos.customer.typeInstead")
              : t("pos.customer.useCamera")}
          </button>
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-slate-700 text-slate-300 text-sm"
          >
            {t("pos.customer.skip")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CustomerScan;
